import type { WorkflowGraph, WorkflowGraphEdge, WorkflowGraphNode, WorkflowStepCatalogItem } from '../../api/workflowRuns'
import { normalizeEdgeId, WORKFLOW_X_GAP, WORKFLOW_Y_BASE } from './workflowGraphModel'

export const WORKFLOW_TYPE_VALUES = ['comic_story', 'image_prompt', 'custom'] as const

export type WorkflowType = (typeof WORKFLOW_TYPE_VALUES)[number]

export type WorkflowTypeMeta = {
  type: WorkflowType
  label: string
  tagline: string
  description: string
  defaultName: string
  defaultDescription: string
  tags: string[]
}

export const WORKFLOW_TYPE_META: Record<WorkflowType, WorkflowTypeMeta> = {
  comic_story: {
    type: 'comic_story',
    label: '漫画故事工作流',
    tagline: '故事、角色、场景并行生成，再汇合产出素材',
    description: '适合从一句故事描述开始，生成漫画故事包，并并行产出人物基础形象与场景图。',
    defaultName: '漫画故事创作工作流',
    defaultDescription: '输入漫画故事想法，自动生成故事设定、人物基础形象与故事场景图。',
    tags: ['漫画', '故事', '生图'],
  },
  image_prompt: {
    type: 'image_prompt',
    label: '提示词生图工作流',
    tagline: '把自然语言整理成提示词后提交生图',
    description: '适合快速验证文本整理、提示词生成和生图链路。',
    defaultName: '提示词生图工作流',
    defaultDescription: '把用户输入整理为高质量提示词，并提交图片生成。',
    tags: ['提示词', '生图'],
  },
  custom: {
    type: 'custom',
    label: '自定义',
    tagline: '从基础节点开始搭建',
    description: '适合高级用户自由组合节点、端口和运行策略。',
    defaultName: '自定义工作流',
    defaultDescription: '',
    tags: ['自定义'],
  },
}

export function workflowTypeOptions() {
  return WORKFLOW_TYPE_VALUES.map((type) => ({
    value: type,
    label: WORKFLOW_TYPE_META[type].label,
  }))
}

export function normalizeWorkflowType(raw: unknown): WorkflowType {
  const value = String(raw ?? '').trim()
  return WORKFLOW_TYPE_VALUES.includes(value as WorkflowType) ? (value as WorkflowType) : 'comic_story'
}

export function workflowTypeFromGraph(graph: Pick<WorkflowGraph, 'inputSchema'> | null | undefined): WorkflowType {
  const schema = (graph?.inputSchema ?? {}) as Record<string, unknown>
  return normalizeWorkflowType(schema.workflowType)
}

export function buildWorkflowInputSchema(type: WorkflowType): Record<string, unknown> {
  if (type === 'image_prompt') {
    return {
      workflowType: type,
      templateVersion: 1,
      fields: [
        {
          key: 'input',
          label: '画面描述',
          type: 'textarea',
          required: true,
          placeholder: '描述你想生成的画面、风格和主体',
        },
      ],
    }
  }
  if (type === 'custom') {
    return {
      workflowType: type,
      templateVersion: 1,
      fields: [
        {
          key: 'input',
          label: '输入',
          type: 'textarea',
          required: true,
          placeholder: '输入工作流运行内容',
        },
      ],
    }
  }
  return {
    workflowType: type,
    templateVersion: 1,
    comicProjectPublicId: '',
    fields: [
      {
        key: 'input',
        label: '故事描述',
        type: 'textarea',
        required: true,
        placeholder: '描述你想创作的漫画故事、角色或世界观',
      },
    ],
  }
}

function configFor(catalog: WorkflowStepCatalogItem[], type: string, fallback: Record<string, unknown>) {
  const item = catalog.find((entry) => entry.type === type)
  return Object.keys(item?.defaultConfig ?? {}).length > 0 ? item!.defaultConfig : fallback
}

function edge(sourceNodeId: string, sourcePort: string, targetNodeId: string, targetPort: string): WorkflowGraphEdge {
  return {
    edgeId: normalizeEdgeId(sourceNodeId, targetNodeId, sourcePort, targetPort),
    sourceNodeId,
    sourcePort,
    targetNodeId,
    targetPort,
    route: sourcePort === 'pass' || sourcePort === 'fail' ? sourcePort : '',
    condition: '',
  }
}

function node(
  id: string,
  type: string,
  name: string,
  description: string,
  x: number,
  y: number,
  config: Record<string, unknown>,
): WorkflowGraphNode {
  return {
    nodeId: id,
    type,
    name,
    description,
    position: { x, y },
    config,
  }
}

export function buildWorkflowTemplateGraph(type: WorkflowType, catalog: WorkflowStepCatalogItem[]): WorkflowGraph {
  if (type === 'custom') {
    return {
      nodes: [
        node('start_1', 'start.input', '开始', '接收运行输入', 80, WORKFLOW_Y_BASE, configFor(catalog, 'start.input', { inputField: 'input' })),
        node('end_1', 'end.output', '结束', '返回工作流结果', WORKFLOW_X_GAP * 2, WORKFLOW_Y_BASE, configFor(catalog, 'end.output', { resultField: 'result' })),
      ],
      edges: [edge('start_1', 'out', 'end_1', 'result')],
      inputSchema: buildWorkflowInputSchema(type),
      outputSchema: {},
    }
  }

  if (type === 'image_prompt') {
    return {
      nodes: [
        node('start_1', 'start.input', '开始', '输入画面描述', 80, WORKFLOW_Y_BASE, configFor(catalog, 'start.input', { inputField: 'input' })),
        node('prompt_1', 'llm.prompt', '提示词整理', '把自然语言整理成生图提示词', 80 + WORKFLOW_X_GAP, WORKFLOW_Y_BASE, {
          ...configFor(catalog, 'llm.prompt', {}),
          outputField: 'prompt',
          userPrompt: '请基于 {{input}} 输出一段可直接用于生图的高质量提示词，保留主体、场景、风格、构图和光影要求。',
        }),
        node('image_1', 'image.generate', '图片生成', '提交图片生成任务', 80 + WORKFLOW_X_GAP * 2, WORKFLOW_Y_BASE, {
          ...configFor(catalog, 'image.generate', {}),
          promptField: 'prompt',
          outputField: 'images',
        }),
        node('end_1', 'end.output', '结束', '输出生成结果', 80 + WORKFLOW_X_GAP * 3, WORKFLOW_Y_BASE, configFor(catalog, 'end.output', { resultField: 'result' })),
      ],
      edges: [
        edge('start_1', 'out', 'prompt_1', 'main'),
        edge('prompt_1', 'out', 'image_1', 'main'),
        edge('image_1', 'out', 'end_1', 'result'),
      ],
      inputSchema: buildWorkflowInputSchema(type),
      outputSchema: {},
    }
  }

  return {
    nodes: [
      node('start_1', 'start.input', '开始', '输入漫画故事想法', 80, WORKFLOW_Y_BASE, configFor(catalog, 'start.input', { inputField: 'input' })),
      node('story_1', 'llm.prompt', '故事创作包', '生成故事梗概、人物、场景和画风约束', 80 + WORKFLOW_X_GAP, WORKFLOW_Y_BASE, {
        ...configFor(catalog, 'llm.prompt', {}),
        outputField: 'prompt',
        userPrompt: '请基于 {{input}} 生成漫画故事创作包，包含故事梗概、主要人物、关键场景、画风建议和需要避免的问题。输出要能直接指导角色基础形象和场景图生成。',
      }),
      node('character_image_1', 'image.generate', '人物基础形象', '根据故事创作包生成人物基础形象', 80 + WORKFLOW_X_GAP * 2, WORKFLOW_Y_BASE - 96, {
        ...configFor(catalog, 'image.generate', {}),
        promptField: 'prompt',
        outputField: 'characterImages',
      }),
      node('scene_image_1', 'image.generate', '故事场景图', '根据故事创作包生成关键场景图', 80 + WORKFLOW_X_GAP * 2, WORKFLOW_Y_BASE + 128, {
        ...configFor(catalog, 'image.generate', {}),
        promptField: 'prompt',
        outputField: 'sceneImages',
      }),
      node('end_1', 'end.output', '结果汇总', '等待人物与场景生成完成后输出', 80 + WORKFLOW_X_GAP * 3, WORKFLOW_Y_BASE, configFor(catalog, 'end.output', { resultField: 'result' })),
    ],
    edges: [
      edge('start_1', 'out', 'story_1', 'main'),
      edge('story_1', 'out', 'character_image_1', 'main'),
      edge('story_1', 'out', 'scene_image_1', 'main'),
      edge('character_image_1', 'out', 'end_1', 'result'),
      edge('scene_image_1', 'out', 'end_1', 'result'),
    ],
    inputSchema: buildWorkflowInputSchema(type),
    outputSchema: {},
  }
}
